import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Box,
  Text,
  Heading,
  VStack,
  HStack,
  Flex,
  useColorModeValue,
  List,
  ListItem,
  Icon,
} from "@chakra-ui/react";
import { FaPhone, FaMapMarkerAlt } from "react-icons/fa";
import { TeamMember } from "../data/teamMembers";
import { brandConfig } from "../brandConfig";
import { HighQualityImage } from "./HighQualityImage";

interface TeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: TeamMember | null;
}

export const TeamMemberModal: React.FC<TeamMemberModalProps> = ({
  isOpen,
  onClose,
  member,
}) => {
  const bgColor = useColorModeValue("white", "gray.900");
  const textColor = useColorModeValue("gray.800", "white");
  const subTextColor = useColorModeValue("gray.600", "gray.400");

  if (!member) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        bg={bgColor}
        w={{ base: "90vw", md: "75vw", lg: "70vw" }}
        h={{ base: "90vh", md: "85vh", lg: "80vh" }}
        maxW="none"
        maxH="none"
        borderRadius="lg"
        overflow="hidden"
        my={{ base: "5vh", md: "7.5vh", lg: "10vh" }}
      >
        <Box
          position="absolute"
          top={6}
          right={6}
          zIndex={10}
          cursor="pointer"
          w="40px"
          h="40px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          onClick={onClose}
          _hover={{ opacity: 0.7 }}
        >
          <Box
            position="relative"
            w="24px"
            h="24px"
          >
            <Box
              position="absolute"
              top="50%"
              left="0"
              w="full"
              h="1px"
              bg={textColor}
              transform="rotate(45deg)"
              transformOrigin="center"
            />
            <Box
              position="absolute"
              top="50%"
              left="0"
              w="full"
              h="1px"
              bg={textColor}
              transform="rotate(-45deg)"
              transformOrigin="center"
            />
          </Box>
        </Box>

        <ModalBody p={0}>
          <Flex direction="row" h="100vh" justify="center" align="flex-start" pt={{ base: 4, md: 8, lg: 16 }}>
            {/* Left side - Photo with proper proportions */}
            <Box
              w={{ base: "40%", md: "42%", lg: "45%" }}
              h={{ base: "300px", md: "400px", lg: "600px" }}
              position="relative"
              overflow="hidden"
              display="flex"
              alignItems="center"
              justifyContent="center"
              p={{ base: 3, md: 4, lg: 6 }}
            >
              {member.image ? (
                <Box
                  w="full"
                  maxW={{ base: "200px", md: "300px", lg: "400px" }}
                  h="auto"
                  aspectRatio="3/4"
                  borderRadius="md"
                  mx="auto"
                  boxShadow="0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                  overflow="hidden"
                  position="relative"
                >
                  <Box
                    w="100%"
                    h="100%"
                    transform={member.imageStyle?.scale ? `scale(${member.imageStyle.scale}) translateY(${member.imageStyle.objectPosition?.includes('90%') ? '7%' : member.imageStyle.objectPosition?.includes('80%') ? '3%' : '0'})` : undefined}
                    transformOrigin="center center"
                  >
                    <HighQualityImage
                      src={member.image}
                      alt={`${member.name} - ${member.title}`}
                      width="100%"
                      height="100%"
                      objectFit="cover"
                      objectPosition="center center"
                    />
                  </Box>
                </Box>
              ) : (
                <Box
                  w="full"
                  maxW={{ base: "200px", md: "300px", lg: "400px" }}
                  aspectRatio="3/4"
                  bg="linear-gradient(135deg, #e8f4f8 0%, #d1e9f0 100%)"
                  position="relative"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="md"
                  mx="auto"
                >
                  {/* Placeholder content */}
                  <Text
                    fontSize="xl"
                    color="gray.500"
                    fontFamily="'Carmen Sans Light', sans-serif"
                  >
                    {member.name}
                  </Text>
                  {/* Placeholder styling effects */}
                  <Box
                    position="absolute"
                    bottom={0}
                    left={0}
                    w="full"
                    h="80%"
                    bg="linear-gradient(to top, rgba(0,0,0,0.1), transparent)"
                    borderRadius="md"
                  />
                </Box>
              )}
              
              {/* Contact info overlay - shown on desktop */}
              <Box
                position="absolute"
                bottom={4}
                right={4}
                display={{ base: "none", lg: "block" }}
              >
                <VStack align="flex-end" spacing={2} bg="rgba(255,255,255,0.9)" p={3} borderRadius="md">
                  {/* Phone */}
                  {member.phone && (
                    <Text
                      fontSize="sm"
                      color={textColor}
                      fontFamily="'Carmen Sans Light', sans-serif"
                      fontWeight="600"
                    >
                      {member.phone}
                    </Text>
                  )}

                  {/* Address */}
                  {member.address && (
                    <VStack align="flex-end" spacing={0}>
                      <Text
                        fontSize="sm"
                        color={textColor}
                        fontFamily="'Carmen Sans Light', sans-serif"
                        fontWeight="600"
                      >
                        {member.address.building}
                      </Text>
                      <Text
                        fontSize="xs"
                        color={textColor}
                        fontFamily="'Carmen Sans Light', sans-serif"
                      >
                        {member.address.level}
                      </Text>
                      <Text
                        fontSize="xs"
                        color={textColor}
                        fontFamily="'Carmen Sans Light', sans-serif"
                      >
                        {member.address.street}
                      </Text>
                      <Text
                        fontSize="xs"
                        color={textColor}
                        fontFamily="'Carmen Sans Light', sans-serif"
                      >
                        {member.address.city} {member.address.state} {member.address.postcode} {member.address.country}
                      </Text>
                    </VStack>
                  )}
                </VStack>
              </Box>
            </Box>

            {/* Right side - Content */}
            <Box
              w={{ base: "60%", md: "58%", lg: "55%" }}
              p={{ base: 2, md: 4, lg: 8 }}
              pr={{ base: 20, md: 16, lg: 12 }}
              overflowY="auto"
              h={{ base: "80vh", md: "85vh", lg: "100vh" }}
              display="flex"
              alignItems="flex-start"
              justifyContent="flex-start"
            >
              <VStack align="flex-start" spacing={{ base: 3, md: 4, lg: 6 }} h="fit-content" maxW={{ base: "75%", md: "400px", lg: "550px" }} w="full" pt={{ base: 2, md: 4, lg: 8 }} pb={{ base: 2, md: 4, lg: 6 }}>
                {/* Name and Title */}
                <Box>
                  <Heading
                    as="h2"
                    fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
                    fontWeight="500"
                    color={textColor}
                    fontFamily="'Carmen Sans Medium', sans-serif"
                    lineHeight="1.2"
                    mb={2}
                  >
                    {member.name}
                  </Heading>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="#9f8f76"
                    fontFamily="'Carmen Sans Light', sans-serif"
                    fontWeight="300"
                  >
                    {member.title}
                  </Text>
                </Box>

                {/* Description */}
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  lineHeight="1.6"
                  fontWeight="300"
                  opacity={0.8}
                >
                  {member.description}
                </Text>

                {/* Background */}
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  lineHeight="1.6"
                  fontWeight="300"
                  opacity={0.8}
                >
                  {member.background}
                </Text>

                {/* Experience */}
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  lineHeight="1.6"
                  fontWeight="300"
                  opacity={0.8}
                >
                  {member.experience.split('$5 - $80 million').length > 1 ? (
                    <>
                      {member.experience.split('$5 - $80 million')[0]}
                      <Text as="span" fontFamily={brandConfig.fonts.body}>
                        $5 - $80 million
                      </Text>
                      {member.experience.split('$5 - $80 million')[1]}
                    </>
                  ) : (
                    member.experience
                  )}
                </Text>

                {/* Qualifications */}
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  lineHeight="1.6"
                  fontWeight="300"
                  opacity={0.8}
                >
                  {member.qualifications}
                </Text>

                {/* Transactions (if any) */}
                {member.transactions && member.transactions.length > 0 && (
                  <Box>
                    <Text
                      fontSize={{ base: "sm", md: "md" }}
                      color={textColor}
                      fontFamily={brandConfig.fonts.body}
                      fontWeight="300"
                      lineHeight="1.6"
                      mb={3}
                    >
                      A selection of major transactions on which {member.name.split(' ')[0]} has advised includes:
                    </Text>
                    <List spacing={2}>
                      {member.transactions.map((transaction, index) => (
                        <ListItem
                          key={index}
                          fontSize={{ base: "sm", md: "md" }}
                          color={textColor}
                          fontFamily={brandConfig.fonts.body}
                          lineHeight="1.6"
                        >
                          <Text as="span" fontWeight="600">
                            {transaction.company}
                          </Text>
                          <Text as="span"> {transaction.description}</Text>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Contact Information - shown on mobile */}
                <Box mt="auto" display={{ base: "block", lg: "none" }}>
                  <VStack align="flex-start" spacing={3}>
                    {/* Phone */}
                    {member.phone && (
                      <HStack spacing={3}>
                        <Icon as={FaPhone as any} color={subTextColor} boxSize={4} />
                        <Text
                          fontSize={{ base: "sm", md: "md" }}
                          color={textColor}
                          fontFamily="'Carmen Sans Light', sans-serif"
                        >
                          {member.phone}
                        </Text>
                      </HStack>
                    )}

                    {/* Address */}
                    {member.address && (
                      <HStack spacing={3} align="flex-start">
                        <Icon as={FaMapMarkerAlt as any} color={subTextColor} boxSize={4} mt={1} />
                        <VStack align="flex-start" spacing={0}>
                          <Text
                            fontSize={{ base: "sm", md: "md" }}
                            color={textColor}
                            fontFamily="'Carmen Sans Light', sans-serif"
                          >
                            {member.address.building}
                          </Text>
                          <Text
                            fontSize={{ base: "sm", md: "md" }}
                            color={textColor}
                            fontFamily="'Carmen Sans Light', sans-serif"
                          >
                            {member.address.level}
                          </Text>
                          <Text
                            fontSize={{ base: "sm", md: "md" }}
                            color={textColor}
                            fontFamily="'Carmen Sans Light', sans-serif"
                          >
                            {member.address.street}
                          </Text>
                          <Text
                            fontSize={{ base: "sm", md: "md" }}
                            color={textColor}
                            fontFamily="'Carmen Sans Light', sans-serif"
                          >
                            {member.address.city} {member.address.state} {member.address.postcode} {member.address.country}
                          </Text>
                        </VStack>
                      </HStack>
                    )}
                  </VStack>
                </Box>
              </VStack>
            </Box>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};